import {
    FormOutlined
} from '@ant-design/icons';
import { Button, Checkbox, Collapse, Input, Select, message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Form, Link, replace, useActionData, useNavigate, useRouteLoaderData } from 'react-router-dom';
import { addPurchaseBillLine, deletePurchaseBillLine, getPurchaseBillVatPreview, updatePurchaseBillDetails, updatePurchaseBillLine } from '../../api/finance';
import { SmallSection } from '../../components/design';
import { CurrencySelectDropDown, DatePicker, ExecutiveEntitySelect, FieldWrapper, GeneralFieldWrapper, InputWrapper } from '../../components/inputs';
import { MismatchPopover } from '../../components/data/mismatch-popover.jsx';
import { ValidationError } from '../../library/classes/exceptions/index.js';
import { generateRouteUrl } from '../../library/constants';
import PaymentMethods from '../../library/dynamics/payment-method';
import { convertDataTypes, getEntityById, getFormattedId } from '../../library/helpers/index.js';
import { getFieldStatus } from '../../library/helpers/forms/index.js';
import { getFieldSource, isDateMismatch, isTextMismatch } from '../../library/helpers/purchase-bill-mismatch.js';
import { formDataToJsonV2 } from '../../library/utilities/formdata.js';
import { formatDate } from '../../library/utilities/intl.js';
import { CancelButton } from '../../ui';
import { PurchaseBillLines } from './purchase-bill-lines.jsx';
import Styles from './purchasebills-basic-details.module.scss';

const mismatchStyle = { backgroundColor: '#ffed09' }

const { Panel } = Collapse

const PurchaseBillBasicDetails = () => {
    const { purchaseBillId, purchaseBillDetails: data, purchaseBillLines, vendorDetails } = useRouteLoaderData('FinancePurchaseBillDetails')
    const extracted = data.extracted_data
    const hasProject = !!data.project_id
    const discountPercent = data.discount != null && data.discount !== ''
        ? data.discount
        : (vendorDetails?.discount != null && vendorDetails?.discount !== '' ? vendorDetails.discount : null)
    const discountDisplay = discountPercent != null ? `${discountPercent}%` : ''

    return (
        <Collapse defaultActiveKey={['1']} expandIconPosition="end">
            <Panel header={<><img src="/icons/headericons/invoicedetail.svg" style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: '8px' }} />Invoice Details</>} key="1" extra={(
                <div onClick={(event) => event.stopPropagation()}>
                    <Link to={generateRouteUrl('UpdatePurchaseBillBasicDetails', {
                        purchaseBillId,
                    })} replace ><FormOutlined /></Link>
                </div>
            )}>
                <div className={Styles.mainWrapper}>
                    <SmallSection colorDark='#8F8F8F'>
                        <div className={Styles.fieldsWrapper}>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Project Id</div>
                                <div className={Styles.DescriptionValue}>
                                    {hasProject ? (
                                        <Link to={generateRouteUrl('FinanceProjectDetails', {
                                            projectId: data.project_id
                                        })}>{getFormattedId(data.project_id)}</Link>
                                    ) : '-'}
                                </div>
                            </div>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Invoice <br /> (Payment Ref)</div>
                                <div className={Styles.DescriptionValue}>
                                    <MismatchPopover sourceInfo={getFieldSource(data.invoice_number, extracted?.invoice_number, false, isTextMismatch)}>
                                        {data.invoice_number}
                                    </MismatchPopover>
                                </div>
                            </div>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Supplier</div>
                                <div className={Styles.DescriptionValue}>
                                    <MismatchPopover sourceInfo={getFieldSource(data?.vendor?.company, extracted?.vendor_name, false, isTextMismatch)}>
                                        <Link to={generateRouteUrl('VendorUpdate', {
                                            id: data.vendor_id
                                        })}>{data?.vendor?.company}</Link>
                                    </MismatchPopover>
                                </div>
                            </div>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Entity</div>
                                <div className={Styles.DescriptionValue}>
                                    <MismatchPopover sourceInfo={getFieldSource(data?.entity?.name, extracted?.entity_name, false, isTextMismatch)}>
                                        {data?.entity?.name}
                                    </MismatchPopover>
                                </div>
                            </div>
                        </div>
                    </SmallSection>
                    <SmallSection colorDark='#8F8F8F'>
                        <div className={Styles.fieldsWrapper}>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Invoice Date</div>
                                <div className={Styles.DescriptionValue}>
                                    <MismatchPopover sourceInfo={getFieldSource(data.invoice_date, extracted?.invoice_date, false, isDateMismatch)} formatValue={formatDate}>
                                        {formatDate(data.invoice_date)}
                                    </MismatchPopover>
                                </div>
                            </div>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Due Date</div>
                                <div className={Styles.DescriptionValue}>
                                    <MismatchPopover sourceInfo={getFieldSource(data.due_date, extracted?.due_date, extracted?.system_defaulted_fields?.includes('due_date'), isDateMismatch)} formatValue={formatDate}>
                                        {formatDate(data.due_date)}
                                    </MismatchPopover>
                                </div>
                            </div>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Currency</div>
                                <div className={Styles.DescriptionValue}>{data.currency?.name}</div>
                            </div>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Payment Method</div>
                                <div className={Styles.DescriptionValue}>{PaymentMethods.find(({ id }) => id === data.payment_method_id)?.name}</div>
                            </div>
                        </div>
                    </SmallSection>
                    <SmallSection colorDark='#8F8F8F'>
                        <div className={Styles.fieldsWrapper}>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Pass on ?</div>
                                <div className={Styles.DescriptionValue}>{(data.pass_on && data.pass_on_entity_id) ? getEntityById(data.pass_on_entity_id)?.name : 'No'}</div>
                            </div>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Propforma</div>
                                <div className={Styles.DescriptionValue}>{data.proforma ? 'Yes' : 'No'}</div>
                            </div>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Discount</div>
                                <div className={Styles.DescriptionValue}>{discountDisplay}</div>
                            </div>
                        </div>
                    </SmallSection>
                    <SmallSection colorDark='#8F8F8F'>
                        <div className={Styles.fieldsWrapper}>
                            <div className={Styles.description}>
                                <div className={Styles.DescriptionTitle}>Description</div>
                                <div className={Styles.DescriptionValue}>{data.description}</div>
                            </div>
                        </div>
                    </SmallSection>
                </div>
                <div className={Styles.mainWrapper}>
                    <PurchaseBillLines edit={false} lines={purchaseBillLines} currency={data.currency} entity_id={data.entity_id} extractedLines={extracted?.lines} />
                </div>
            </Panel>
        </Collapse>
    )
}
PurchaseBillBasicDetails.Actions = {
    addLine: async ({ params, data }) => {
        const payload = convertDataTypes(data, {
            // general_ledger_id: parseInt,
            quantity: parseFloat,
            vat_type_id: parseInt,
            price_excl_vat: Number
        })
        try {
            const msg = await addPurchaseBillLine(params.purchaseBillId, payload)
            message.success(msg)
            return true
        }
        catch (error) {
            message.error(error.message)
            if (error instanceof ValidationError) {
                return {
                    errors: error.errors
                }
            }
        }
        return false
    },
    deleteLine: async ({ params, data }) => {
        const { lineId } = data
        try {
            const msg = await deletePurchaseBillLine(params.purchaseBillId, lineId)
            message.success(msg)
            return true
        }
        catch (error) {
            message.error(error.message)
        }
        return false
    },
}

const computeDueDateFromInvoiceAndTerm = (invoiceDate, paymentTermDays) => {
    if (!invoiceDate || paymentTermDays === null || paymentTermDays === undefined || paymentTermDays === '') {
        return null
    }
    const days = parseInt(paymentTermDays, 10)
    if (Number.isNaN(days)) return null
    const match = String(invoiceDate).match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return null
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    if (Number.isNaN(date.getTime())) return null
    date.setDate(date.getDate() + days)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const UpdatePurchaseBillBasicDetails = () => {
    const { purchaseBillId, purchaseBillDetails, purchaseBillLines } = useRouteLoaderData('FinancePurchaseBillDetails')
    const extracted = purchaseBillDetails.extracted_data
    const navigate = useNavigate()
    const [paymentMethodId, setPaymentMethodId] = useState(purchaseBillDetails.payment_method_id)
    const [passOn, setPassOn] = useState(!!purchaseBillDetails.pass_on)
    const [invoiceNumber, setInvoiceNumber] = useState(purchaseBillDetails.invoice_number)
    const [invoiceDate, setInvoiceDate] = useState(purchaseBillDetails.invoice_date)
    const [dueDate, setDueDate] = useState(purchaseBillDetails.due_date)
    const [dueDateAutoComputed, setDueDateAutoComputed] = useState(false)
    const [entityId, setEntityId] = useState(purchaseBillDetails.entity_id)
    const [vatPreview, setVatPreview] = useState({})
    const { errors = null } = useActionData() || {};

    // Re-resolve every line's vat_type/general_ledger for the newly picked
    // entity via the server (Purchase_bill.getVendorVatType, same logic
    // linkProject uses) — skip the very first run so a bill loaded with a
    // pre-existing mismatch isn't silently rewritten before the user acts.
    const hasMountedEntityRef = useRef(false)
    useEffect(() => {
        if (!hasMountedEntityRef.current) {
            hasMountedEntityRef.current = true
            return
        }
        if (entityId == null) return
        let cancelled = false
        getPurchaseBillVatPreview(purchaseBillId, entityId)
            .then((preview) => {
                if (cancelled) return
                const byLineId = Object.fromEntries((preview || []).map((row) => [row.line_id, row]))
                setVatPreview(byLineId)
            })
            .catch(() => {
                if (!cancelled) setVatPreview({})
            })
        return () => { cancelled = true }
    }, [entityId])

    const handleInvoiceDateChange = (value) => {
        setInvoiceDate(value)
        const computedDue = computeDueDateFromInvoiceAndTerm(
            value,
            purchaseBillDetails?.vendor?.payment_term
        )
        if (computedDue) {
            setDueDate(computedDue)
            setDueDateAutoComputed(true)
        }
    }

    const handleDueDateChange = (value) => {
        setDueDate(value)
        setDueDateAutoComputed(false)
    }

    return (
        <>
            <Collapse defaultActiveKey={['1']} expandIconPosition="end">
                <Panel header={<><img src="/icons/headericons/invoicedetail.svg" style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: '8px' }} />Invoice Details</>} key="1">
                    <Form id="formFill" method="post" className={Styles['fieldsWrapper--Four']}>
                        <input type="hidden" name="action" value="updateDetails" />
                        <FieldWrapper name="invoice_number" label="Invoice (Payment Reference)" errors={errors} showRequired>
                            <MismatchPopover sourceInfo={getFieldSource(invoiceNumber, extracted?.invoice_number, false, isTextMismatch)}>
                                <Input
                                    name="invoice_number"
                                    value={invoiceNumber}
                                    onChange={(e) => setInvoiceNumber(e.target.value)}
                                    status={getFieldStatus('invoice_number', errors)}
                                    required
                                    style={isTextMismatch(invoiceNumber, extracted?.invoice_number) ? { ...mismatchStyle, width: '100%' } : { width: '100%' }}
                                />
                            </MismatchPopover>
                        </FieldWrapper>
                        <FieldWrapper name="entity_id" label="Entity" errors={errors}>
                            <MismatchPopover sourceInfo={getFieldSource(getEntityById(entityId)?.name, extracted?.entity_name, false, isTextMismatch)}>
                                <ExecutiveEntitySelect
                                    name="entity_id"
                                    value={entityId}
                                    onChange={setEntityId}
                                    style={isTextMismatch(getEntityById(entityId)?.name, extracted?.entity_name) ? mismatchStyle : undefined}
                                />
                            </MismatchPopover>
                        </FieldWrapper>
                        <FieldWrapper name="invoice_date" label="Invoice Date" errors={errors} showRequired>
                            <MismatchPopover sourceInfo={getFieldSource(invoiceDate, extracted?.invoice_date, false, isDateMismatch)} formatValue={formatDate}>
                                <DatePicker
                                    name="invoice_date"
                                    value={invoiceDate}
                                    onChange={handleInvoiceDateChange}
                                    useHidden
                                    disablePastDates={false}
                                    disabledBefore="1900-01-01"
                                    style={isDateMismatch(invoiceDate, extracted?.invoice_date) ? mismatchStyle : undefined}
                                />
                            </MismatchPopover>
                        </FieldWrapper>
                        <FieldWrapper name="due_date" label="Due Date" errors={errors}>
                            <MismatchPopover sourceInfo={getFieldSource(dueDate, extracted?.due_date, dueDateAutoComputed || extracted?.system_defaulted_fields?.includes('due_date'), isDateMismatch)} formatValue={formatDate}>
                                <DatePicker
                                    name="due_date"
                                    value={dueDate}
                                    onChange={handleDueDateChange}
                                    useHidden
                                    disablePastDates={false}
                                    disabledBefore="1900-01-01"
                                    style={isDateMismatch(dueDate, extracted?.due_date) ? mismatchStyle : undefined}
                                />
                            </MismatchPopover>
                        </FieldWrapper>
                        <FieldWrapper name="currency_id" label="Currency" errors={errors}>
                            <CurrencySelectDropDown value={purchaseBillDetails.currency_id} disabled />
                        </FieldWrapper>
                        <FieldWrapper name="payment_method_id" label="Payment method" errors={errors}>
                            <Select
                                options={PaymentMethods}
                                value={paymentMethodId}
                                onChange={(v) => {
                                    setPaymentMethodId(v)
                                }}
                                fieldNames={{ value: 'id', label: 'name' }}
                            />
                            <input type="hidden" name="payment_method_id" value={paymentMethodId} />
                        </FieldWrapper>
                        <GeneralFieldWrapper style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '1rem' }}>
                            <FieldWrapper name="pass_on" label="Pass on ?" errors={errors}>
                                <Checkbox name="pass_on" defaultChecked={!!purchaseBillDetails.pass_on} onChange={e => setPassOn(e.target.checked)} />
                                <input type="hidden" name="pass_on" value={passOn} />
                            </FieldWrapper>
                            <FieldWrapper name="pass_on_entity_id" label="Pass on Entity" errors={errors}>
                                <ExecutiveEntitySelect name="pass_on_entity_id" defaultValue={purchaseBillDetails.pass_on_entity_id} disabled={!passOn} />
                            </FieldWrapper>
                        </GeneralFieldWrapper>
                        <FieldWrapper name="proforma" label="Proforma" errors={errors}>
                            <input type="hidden" name="proforma" value={false} />
                            <Checkbox name="proforma" defaultChecked={purchaseBillDetails.proforma} valuePropName="checked" />
                        </FieldWrapper>
                        <InputWrapper
                            name="description"
                            label="Description"
                            defaultValue={purchaseBillDetails.description}
                            span={4}
                            // defaultValue={item ?? undefined}
                            textarea
                        />

                        <div className={Styles.mainWrapper_bill}>
                            <PurchaseBillLines edit={true} lines={purchaseBillLines} currency={purchaseBillDetails.currency} entity_id={entityId} errors={errors?.purchaseLine} extractedLines={extracted?.lines} vatPreview={vatPreview} />
                        </div>
                        <div className={Styles.actionButton}>
                            <Button type="primary" htmlType='submit'>Save</Button>
                            <CancelButton onClick={() => navigate('..', { replace: true })}>Cancel</CancelButton>
                        </div>
                    </Form>
                </Panel>
            </Collapse>
        </>
    )
}

UpdatePurchaseBillBasicDetails.Actions = {
    updateLine: async ({ params, data }) => {
        console.log({ data })
        const { line_id, ...payload } = convertDataTypes(data, {
            line_id: parseInt,
            // general_ledger_id: parseInt,
            quantity: parseFloat,
            vat_type_id: parseInt,
            price_excl_vat: Number
        })
        try {
            const msg = await updatePurchaseBillLine(params.purchaseBillId, line_id, payload)
            message.success(msg)
            return true
        }
        catch (error) {
            message.error(error.message)
            if (error instanceof ValidationError) {
                return {
                    errors: error.errors
                }
            }
        }
        return false
    },

    updateDetails: async ({ params, data }) => {
        const payload = convertDataTypes(formDataToJsonV2(data), {
            entity_id: parseInt,
            pass_on_entity_id: parseInt,
            payment_method_id: parseInt,
            pass_on: (val) => val === 'true' ? 1 : 0,
            proforma: (val) => val === 'true' ? 1 : 0,
            purchaseLine: (lines) => Object.values(lines).map(line => convertDataTypes(line, ({
                id: parseInt,
                price_excl_vat: parseFloat,
                quantity: parseFloat,
                vat_type_id: parseInt,
                general_ledger_id: parseInt,
            })))
        })

        try {
            const msg = await updatePurchaseBillDetails(params.purchaseBillId, payload)
            message.success(msg)
            return replace(generateRouteUrl('FinancePurchaseBillDetails', {
                purchaseBillId: params.purchaseBillId
            }))
        }
        catch (error) {
            message.error(error.message)
            if (error instanceof ValidationError) {
                return {
                    errors: error.getNestedErrors()
                }
            }
        }
        return false
    },

}

export { PurchaseBillBasicDetails, UpdatePurchaseBillBasicDetails };
